/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {
    RoleDtoWithAcces,
    UserRoleInfo,
    RoleInfo
} from 'modules/admin-roles-management/models/role-management.model';
import { MotifSelect, MotifOption, MotifAvatar } from '@ey-xd/motif-react';

import { RootState } from 'common/redux/core/root.reducer';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormField,
    Label,
    Input,
    DataList
} from 'common/components';

import {
    getAddUserModalVisibility,
    getPermissionEditData
} from 'modules/user-management/redux/user.selector';
import { userActions } from 'modules/user-management/redux/user.slice';

import './AddUserModal.scss';
import { getAllRoles } from 'modules/admin-roles-management/utils/role-management.utils';
import { getUserRoleAssignmentHeadersData } from 'modules/user-management/config/user.config';
import {
    assignUserRoleUtility,
    getUserOptionData
} from 'modules/user-management/utils/user-permission.utils';
import { Action } from 'common/enums';
import { AssignRoleApiPayload } from 'modules/user-management/models/user.model';

const AddUserModal: FC = () => {
    const dispatch = useDispatch();
    const editData: UserRoleInfo = useSelector((state: RootState) => getPermissionEditData(state));
    const [selectedInputValue, setSelectedInputValue] = useState(
        editData ? editData.emailAddress : []
    );
    const [allRolesData, setAllRolesData] = useState<RoleDtoWithAcces[]>([]);
    const modalVisible: boolean = useSelector((state: RootState) =>
        getAddUserModalVisibility(state)
    );
    const [selectedRoles, setSelectedRoles] = useState<string[]>(
        editData
            ? editData?.applicationRoles.map((ele: RoleInfo) => ele.applicationRoleUserId) ?? []
            : []
    );
    const gbbUserList = useSelector((state: RootState) => state.user.userList.data);
    const disableEditing = selectedInputValue.length == 0 || selectedRoles.length == 0;

    const onSucces = (status: boolean) => {
        if (status) {
            dispatch(userActions.setAddUserModalVisibility(false) as any);
            getAllRoles(dispatch, setAllRolesData);
        }
    };

    const handleAddEditRole = async () => {
        const data: AssignRoleApiPayload = {
            usersEmailList: editData ? [editData.emailAddress] : selectedInputValue,
            applicationRoleIds: selectedRoles
        };
        assignUserRoleUtility(data, dispatch, onSucces, Action.Create);
    };

    const handleCancel = () => {
        dispatch(userActions.resetPermissionEditData() as any);
        dispatch(userActions.setAddUserModalVisibility(false) as any);
    };

    const addEmploy = (val: any) => {
        setSelectedInputValue(val);
    };

    const handleAction = (value: boolean, data: RoleDtoWithAcces) => {
        const updatedData = { ...data, access: value };
        setSelectedRoles((prevState) => {
            if (prevState.includes(data.applicationRoleId)) {
                return prevState.filter((ele) => ele !== data.applicationRoleId);
            } else {
                return [...prevState, data.applicationRoleId];
            }
        });
        setAllRolesData((prevState) => {
            const newData = prevState.map((ele) => {
                if (ele.applicationRoleId === data.applicationRoleId) {
                    return updatedData;
                }
                return ele;
            });
            return newData;
        });
    };

    useEffect(() => {
        dispatch(userActions.getUserList());
        if (allRolesData.length <= 0) {
            getAllRoles(dispatch, setAllRolesData, editData);
        } else {
            getAllRoles(dispatch, setAllRolesData);
        }
        return () => {
            dispatch(userActions.resetPermissionEditData() as any);
            setSelectedInputValue([]);
            setAllRolesData([]);
            setSelectedRoles([]);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <Modal
                style={{ backgroundColor: '#2E2E3C', boxShadow: '#2E2E3C' }}
                show={modalVisible}
                onClose={handleCancel}>
                <ModalHeader style={{ borderBottom: 'none' }}>
                    {`${editData ? 'Edit User Permission' : 'Assign User Permission'}`}
                </ModalHeader>
                <ModalBody className="modal-body">
                    <p className="font-style label-padding">Search User</p>
                    {editData ? (
                        <FormField>
                            <Label>{editData.emailAddress}</Label>
                            <Input type="email" disabled value={editData.emailAddress} />
                        </FormField>
                    ) : (
                        <FormField className="users">
                            {gbbUserList && (
                                <>
                                    <Label id="select-label-users">Search user...</Label>
                                    <MotifSelect
                                        value={selectedInputValue}
                                        multiple
                                        filter
                                        onChange={(val: any) => addEmploy(val)}
                                        aria-label="Search"
                                        searchPlaceholder="Search with Email">
                                        {gbbUserList.map((item: any) => (
                                            <MotifOption
                                                key={item.emailAddress}
                                                value={item.emailAddress}
                                                label={item.emailAddress}>
                                                <>
                                                    <MotifAvatar
                                                        userName={getUserOptionData(item)}
                                                        status={null}
                                                    />
                                                    {`${item.firstName} ${item.lastName}`}
                                                </>
                                            </MotifOption>
                                        ))}
                                    </MotifSelect>
                                </>
                            )}
                        </FormField>
                    )}
                    <p className="font-style label-padding">Roles</p>
                    <DataList
                        isNoDataVisible={allRolesData?.length === 0}
                        data={allRolesData ?? []}
                        columnDefs={getUserRoleAssignmentHeadersData(handleAction, false)}
                        suppressDrag={true}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        size="medium"
                        type="button"
                        disabled={disableEditing}
                        onClick={handleAddEditRole}
                        className="modal-button">
                        {`${editData ? 'Update' : 'Assign'}`}
                    </Button>
                    <Button
                        size="medium"
                        className="modal-button-cancel"
                        variant="secondary"
                        type="button"
                        onClick={handleCancel}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default AddUserModal;
